import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  ArrowRightLeft,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleSlash,
  ExternalLink,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  XCircle,
} from 'lucide-react'
import api from '@/lib/api.ts'
import { useAnonymize } from '@/hooks/useAnonymize.ts'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  FilterBar,
  FilterChip,
  Input,
  LoadingState,
  Modal,
  PageHeader,
} from '@/components/ui'
import {
  MapCustomerModal,
  TestCreateCustomerModal,
} from './SupplierCustomerDialogs.tsx'
import {
  CustomerMasterDataSync,
  PORTAL_LABEL,
  countPushableDiffs,
  useBulkMasterDataSyncPush,
  useMasterDataSyncOverview,
  type BulkPushState,
  type CustomerComparison,
  type FieldDiff,
  type FieldKey,
  type Portal,
} from './MasterDataSyncPanel.tsx'

type SupplierId = 'sap' | 'also' | 'crayon' | 'eset' | 'hornet' | 'cwa' | 'kyberio'

interface SupplierProduct {
  supplierId: SupplierId
  productName: string
  externalId?: string
  detail?: string
  status?: 'active' | 'warning' | 'danger' | 'unknown'
  unassigned?: boolean
  externalCustomerName?: string
  externalCustomerId?: string
}

interface SupplierStatus {
  id: SupplierId
  label: string
  configured: boolean
  available: boolean
  error?: string
  /** Verbindung funktioniert, aber Daten sind eingeschraenkt (z.B. Permission). */
  warning?: string
  productCount: number
  customerCount: number
  unassignedCount: number
}

interface CustomerWithProducts {
  id: string
  name: string
  isActive: boolean
  /** Lokale SHM-customer.id (UUID) — nur gesetzt, wenn ein DB-Eintrag existiert. */
  shmId?: string
  externalIds: {
    eset?: string | null
    also?: string | null
    cwa?: string | null
    sap?: string | null
    tanss?: string | null
  }
  products: SupplierProduct[]
}

interface SupplierOverviewResponse {
  suppliers: SupplierStatus[]
  customers: CustomerWithProducts[]
  unassigned: {
    also: SupplierProduct[]
    eset: SupplierProduct[]
    cwa: SupplierProduct[]
    kyberio?: SupplierProduct[]
  }
  meta?: {
    lastSapSyncedAt: string | null
  }
}

const SUPPLIER_ORDER: SupplierId[] = ['sap', 'also', 'crayon', 'eset', 'hornet', 'cwa', 'kyberio']

const SUPPLIER_DOT_CLASS: Record<SupplierId, string> = {
  sap: 'bg-slate-700',
  also: 'bg-blue-500',
  crayon: 'bg-orange-500',
  eset: 'bg-green-500',
  hornet: 'bg-yellow-500',
  cwa: 'bg-purple-500',
  kyberio: 'bg-teal-500',
}

function statusIcon(s: SupplierStatus) {
  if (!s.available) return <CircleSlash className="h-4 w-4 text-gray-400" />
  if (!s.configured) return <CircleSlash className="h-4 w-4 text-gray-400" />
  if (s.error) return <XCircle className="h-4 w-4 text-red-500" />
  if (s.warning) return <AlertTriangle className="h-4 w-4 text-amber-500" />
  return <CheckCircle2 className="h-4 w-4 text-green-500" />
}

function statusLabel(s: SupplierStatus) {
  if (!s.available) return 'Anbindung ausstehend'
  if (!s.configured) return 'Nicht konfiguriert'
  if (s.error) return 'Fehler beim Laden'
  if (s.warning) return 'Verbunden · eingeschraenkt'
  return 'Verbunden'
}

function productStatusBadge(p: SupplierProduct) {
  switch (p.status) {
    case 'danger':
      return (
        <Badge variant="danger" size="sm">
          Abgelaufen
        </Badge>
      )
    case 'warning':
      return (
        <Badge variant="warning" size="sm">
          Achtung
        </Badge>
      )
    case 'active':
      return (
        <Badge variant="success" size="sm">
          Aktiv
        </Badge>
      )
    default:
      return null
  }
}

export function LieferantenPage() {
  const a = useAnonymize()
  const [search, setSearch] = useState('')
  const [activeSuppliers, setActiveSuppliers] = useState<Set<SupplierId>>(new Set())
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [showOnlyWithProducts, setShowOnlyWithProducts] = useState(true)
  const [testCreatePortal, setTestCreatePortal] = useState<'also' | 'eset' | null>(null)
  const [mapTarget, setMapTarget] = useState<
    { portal: 'also' | 'eset'; portalId: string; portalName?: string } | null
  >(null)
  const [refreshing, setRefreshing] = useState(false)

  const query = useQuery<SupplierOverviewResponse>({
    queryKey: ['supplier-overview'],
    queryFn: () => api.get('/einkauf/supplier-overview').then((r) => r.data),
    retry: false,
  })

  // Stammdaten-Abgleich SAP ↔ MSP-Portale: pro Kunde Diffs + Push-Aktionen.
  // Wird parallel zur Uebersicht geladen und im aufgeklappten Eintrag angezeigt.
  const syncQuery = useMasterDataSyncOverview()
  const syncByShmId = useMemo(() => {
    const map = new Map<string, CustomerComparison>()
    for (const c of syncQuery.data?.customers ?? []) {
      map.set(c.customerId, c)
    }
    return map
  }, [syncQuery.data])

  // Bulk-Click-Thru: alle Kunden mit Abweichungen sequentiell pushen.
  const bulkPush = useBulkMasterDataSyncPush()
  const customersWithDiffs = useMemo(
    () =>
      (syncQuery.data?.customers ?? []).filter((c) => countPushableDiffs(c) > 0),
    [syncQuery.data],
  )
  const totalDiffs = useMemo(
    () => customersWithDiffs.reduce((s, c) => s + countPushableDiffs(c), 0),
    [customersWithDiffs],
  )

  const suppliers = useMemo<SupplierStatus[]>(() => {
    const map = new Map(query.data?.suppliers.map((s) => [s.id, s] as const) ?? [])
    return SUPPLIER_ORDER.map(
      (id) =>
        map.get(id) ?? {
          id,
          label: id.toUpperCase(),
          configured: false,
          available: false,
          productCount: 0,
          customerCount: 0,
          unassignedCount: 0,
        },
    )
  }, [query.data])

  const filteredCustomers = useMemo(() => {
    if (!query.data) return []
    const term = search.trim().toLowerCase()
    return query.data.customers.filter((c) => {
      if (term && !c.name.toLowerCase().includes(term)) return false
      if (activeSuppliers.size > 0) {
        const hit = c.products.some((p) => activeSuppliers.has(p.supplierId))
        if (!hit) return false
      }
      if (showOnlyWithProducts && c.products.length === 0) return false
      return true
    })
  }, [query.data, search, activeSuppliers, showOnlyWithProducts])

  function toggleSupplier(id: SupplierId) {
    setActiveSuppliers((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleExpanded(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function expandAll() {
    setExpanded(new Set(filteredCustomers.map((c) => c.id)))
  }
  function collapseAll() {
    setExpanded(new Set())
  }

  async function refreshFromSap() {
    setRefreshing(true)
    try {
      // Triggert den SAP-Master-Sync (laedt alle BPs aus SAP und upsertet in
      // die customers-Tabelle). Anschliessend Refetch der Lieferanten-Uebersicht
      // — diese liest dann aus der frischen DB.
      await api.post('/einkauf/master-sync/run', { sources: ['sap'] })
      await Promise.all([query.refetch(), syncQuery.refetch()])
    } finally {
      setRefreshing(false)
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Lieferanten / MSP"
        description="Übersicht aller Lieferanten-Anbindungen und der zugeordneten Kundenprodukte."
        breadcrumbs={[{ label: 'Einkauf' }, { label: 'Lieferanten / MSP' }]}
        actions={
          <div className="flex items-center gap-2">
            {query.data?.meta?.lastSapSyncedAt ? (
              <span
                className="text-caption text-gray-500"
                title={new Date(query.data.meta.lastSapSyncedAt).toLocaleString('de-DE')}
              >
                Sync vor {formatRelativeAge(query.data.meta.lastSapSyncedAt)}
              </span>
            ) : query.data ? (
              <span className="text-caption text-amber-700">
                Stammdaten noch nicht synchronisiert
              </span>
            ) : null}
            <Button
              variant="secondary"
              size="sm"
              onClick={refreshFromSap}
              disabled={refreshing}
              title="Lädt alle SAP-Geschäftspartner aus SAP B1 und persistiert sie in die lokale customers-Tabelle. Anschließend werden Lieferanten-Daten aus der DB statt live aus SAP gelesen."
            >
              <RefreshCw
                className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`}
              />
              {refreshing ? 'Synchronisiere…' : 'Stammdaten neu laden'}
            </Button>
            {customersWithDiffs.length > 0 ? (
              <Button
                variant="primary"
                size="sm"
                onClick={() => bulkPush.open(customersWithDiffs)}
                disabled={bulkPush.phase === 'running'}
                title={`${customersWithDiffs.length} Kunde(n) mit insgesamt ${totalDiffs} pushbaren Abweichungen — Vorschau anzeigen, dann übertragen.`}
              >
                <ArrowRightLeft className="h-4 w-4 mr-1" />
                {customersWithDiffs.length} Kunde{customersWithDiffs.length === 1 ? '' : 'n'} abgleichen ({totalDiffs})
              </Button>
            ) : null}
          </div>
        }
      />

      {/* Lieferanten-Status-Karten */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {suppliers.map((s) => {
          const isActive = activeSuppliers.has(s.id)
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => s.available && toggleSupplier(s.id)}
              disabled={!s.available}
              className={`text-left rounded-lg border bg-white px-3 py-3 shadow-card transition ${
                isActive
                  ? 'border-brand-400 ring-2 ring-brand-200'
                  : s.available
                    ? 'border-gray-200 hover:border-gray-300'
                    : 'border-gray-200 opacity-60 cursor-not-allowed'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className={`h-2.5 w-2.5 rounded-full ${SUPPLIER_DOT_CLASS[s.id]}`} />
                  <span className="font-medium text-gray-900">{s.label}</span>
                </div>
                {statusIcon(s)}
              </div>
              <div className="mt-1.5 text-caption text-gray-500">{statusLabel(s)}</div>
              {s.available && s.configured && !s.error && !s.warning ? (
                <div className="mt-2 grid grid-cols-2 gap-1 text-caption">
                  <div>
                    <div className="text-gray-500">Kunden</div>
                    <div className="text-gray-900 font-semibold tabular-nums">
                      {s.customerCount}
                    </div>
                  </div>
                  {s.id !== 'sap' ? (
                    <div>
                      <div className="text-gray-500">Produkte</div>
                      <div className="text-gray-900 font-semibold tabular-nums">
                        {s.productCount}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
              {s.error ? (
                <div className="mt-2 text-caption text-red-600 line-clamp-2" title={s.error}>
                  {s.error}
                </div>
              ) : null}
              {s.warning && !s.error ? (
                <div
                  className="mt-2 text-caption text-amber-700 line-clamp-3"
                  title={s.warning}
                >
                  {s.warning}
                </div>
              ) : null}
              {s.unassignedCount > 0 ? (
                <div className="mt-2 text-caption text-amber-700 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  {s.unassignedCount} ohne Kundenzuordnung
                </div>
              ) : null}
              {(s.id === 'also' || s.id === 'eset') && s.configured && !s.error ? (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation()
                    setTestCreatePortal(s.id as 'also' | 'eset')
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      e.stopPropagation()
                      setTestCreatePortal(s.id as 'also' | 'eset')
                    }
                  }}
                  className="mt-2 inline-flex items-center gap-1 text-caption text-brand-700 hover:text-brand-900 hover:underline cursor-pointer"
                >
                  <Plus className="h-3 w-3" />
                  Testkunde anlegen
                </span>
              ) : null}
            </button>
          )
        })}
      </div>

      {/* Filter */}
      <FilterBar>
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Kunde suchen…"
            className="pl-8"
          />
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {SUPPLIER_ORDER.map((id) => {
            const s = suppliers.find((x) => x.id === id)
            if (!s) return null
            return (
              <FilterChip
                key={id}
                active={activeSuppliers.has(id)}
                disabled={!s.available}
                onClick={() => s.available && toggleSupplier(id)}
                count={s.customerCount > 0 ? s.customerCount : null}
                leadingIcon={<span className={`h-2 w-2 rounded-full ${SUPPLIER_DOT_CLASS[id]}`} />}
              >
                {s.label}
              </FilterChip>
            )
          })}
        </div>
        <div className="ml-auto flex items-center gap-2 text-small text-gray-600">
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showOnlyWithProducts}
              onChange={(e) => setShowOnlyWithProducts(e.target.checked)}
              className="rounded border-gray-300"
            />
            Nur Kunden mit Produkten
          </label>
          <button
            type="button"
            onClick={expandAll}
            className="text-brand-600 hover:text-brand-700 text-caption"
          >
            Alle aufklappen
          </button>
          <span className="text-gray-300">·</span>
          <button
            type="button"
            onClick={collapseAll}
            className="text-brand-600 hover:text-brand-700 text-caption"
          >
            Alle zuklappen
          </button>
        </div>
      </FilterBar>

      {/* Kunden-Tree */}
      {query.isLoading ? (
        <LoadingState label="Lieferantendaten werden geladen…" />
      ) : query.isError ? (
        <ErrorState
          title="Fehler beim Laden"
          description="Die Lieferantenübersicht konnte nicht geladen werden."
          details={query.error instanceof Error ? query.error.message : String(query.error)}
        />
      ) : filteredCustomers.length === 0 ? (
        <EmptyState
          title="Keine Kunden gefunden"
          description={
            showOnlyWithProducts
              ? 'Keine Kunden mit Produkten beim aktuellen Filter. Filter anpassen oder „Nur Kunden mit Produkten" deaktivieren.'
              : 'Mit den aktuellen Filterkriterien gibt es keine Treffer.'
          }
        />
      ) : (
        <Card>
          <ul className="divide-y divide-gray-100">
            {filteredCustomers.map((c) => {
              const isOpen = expanded.has(c.id)
              const grouped = groupBySupplier(c.products, activeSuppliers)
              const sync = c.shmId ? syncByShmId.get(c.shmId) : undefined
              const diffCount = sync ? countPushableDiffs(sync) : 0
              return (
                <li key={c.id}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleExpanded(c.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        toggleExpanded(c.id)
                      }
                    }}
                    className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {isOpen ? (
                        <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      )}
                      <span className="font-medium text-gray-900 truncate">{a.name(c.name)}</span>
                      {!c.isActive ? (
                        <Badge variant="neutral" size="sm">
                          inaktiv
                        </Badge>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {sync && diffCount > 0 ? (
                        <Button
                          variant="primary"
                          size="xs"
                          onClick={(e) => {
                            e.stopPropagation()
                            bulkPush.open([sync])
                          }}
                          disabled={bulkPush.phase === 'running'}
                          title={`${diffCount} Stammdaten-Abweichung${diffCount === 1 ? '' : 'en'} — alle SAP-Werte in die verlinkten Portale schreiben`}
                        >
                          <ArrowRightLeft className="h-3 w-3 mr-1" />
                          Stammdaten pushen ({diffCount})
                        </Button>
                      ) : null}
                      {SUPPLIER_ORDER.map((sid) => {
                        const count = grouped.get(sid)?.length ?? 0
                        if (count === 0) return null
                        return (
                          <span
                            key={sid}
                            className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-caption text-gray-700"
                            title={`${count} Produkt(e) bei ${suppliers.find((s) => s.id === sid)?.label ?? sid}`}
                          >
                            <span
                              className={`h-1.5 w-1.5 rounded-full ${SUPPLIER_DOT_CLASS[sid]}`}
                            />
                            <span className="tabular-nums">{count}</span>
                          </span>
                        )
                      })}
                      {c.products.length === 0 ? (
                        <span className="text-caption text-gray-400">keine Produkte</span>
                      ) : null}
                    </div>
                  </div>

                  {isOpen ? (
                    <div className="bg-gray-50/50 border-t border-gray-100 px-4 py-3 space-y-3">
                      {c.products.length === 0 ? (
                        <p className="text-small text-gray-500">
                          Keine Produkte bei einem der konfigurierten Lieferanten gefunden.
                        </p>
                      ) : (
                        Array.from(grouped.entries()).map(([sid, products]) => {
                          const supplier = suppliers.find((s) => s.id === sid)
                          return (
                            <div key={sid}>
                              <div className="flex items-center gap-2 mb-1.5">
                                <span
                                  className={`h-2 w-2 rounded-full ${SUPPLIER_DOT_CLASS[sid]}`}
                                />
                                <span className="text-caption font-medium text-gray-700 uppercase tracking-wide">
                                  {supplier?.label ?? sid}
                                </span>
                                <span className="text-caption text-gray-400">
                                  {products.length}
                                </span>
                              </div>
                              <ul className="ml-4 space-y-1">
                                {products.map((p, idx) => (
                                  <li
                                    key={`${p.externalId ?? 'noid'}-${idx}`}
                                    className="flex items-center justify-between gap-3 text-small"
                                  >
                                    <span className="text-gray-900 truncate">{p.productName}</span>
                                    <span className="flex items-center gap-2 text-gray-500 flex-shrink-0">
                                      {p.detail ? (
                                        <span className="tabular-nums">{p.detail}</span>
                                      ) : null}
                                      {productStatusBadge(p)}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )
                        })
                      )}
                      <div className="pt-2 text-caption text-gray-500 flex flex-wrap gap-x-3 gap-y-1">
                        {c.externalIds.tanss ? <span>TANSS: {c.externalIds.tanss}</span> : null}
                        {c.externalIds.sap ? <span>SAP: {c.externalIds.sap}</span> : null}
                        {c.externalIds.eset ? <span>ESET: {c.externalIds.eset}</span> : null}
                        {c.externalIds.also ? <span>ALSO: {c.externalIds.also}</span> : null}
                        {c.externalIds.cwa ? <span>CWA: {c.externalIds.cwa}</span> : null}
                      </div>

                      {/* Stammdaten-Abgleich SAP ↔ MSP-Portal — wird immer
                          gerendert, mit klarem Status-Hinweis falls noch
                          keine Vergleichsdaten vorliegen. So ist das Feature
                          auch dann sichtbar, wenn der Kunde keine
                          DB-Verknuepfung zu einem Portal hat. */}
                      <div className="pt-2 border-t border-gray-200">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-caption font-medium text-gray-700 uppercase tracking-wide">
                            Stammdaten-Abgleich SAP ↔ MSP
                          </span>
                          {sync && sync.portals.length > 0 ? (
                            diffCount > 0 ? (
                              <Badge variant="warning" size="sm">
                                {diffCount} Abweichung{diffCount === 1 ? '' : 'en'}
                              </Badge>
                            ) : (
                              <Badge variant="success" size="sm">
                                synchron
                              </Badge>
                            )
                          ) : (
                            <Badge variant="neutral" size="sm">
                              kein Vergleich verfügbar
                            </Badge>
                          )}
                        </div>
                        {sync && sync.portals.length > 0 ? (
                          <CustomerMasterDataSync comparison={sync} />
                        ) : (
                          <MasterDataSyncEmpty
                            isLoading={syncQuery.isLoading}
                            isError={syncQuery.isError}
                            customer={c}
                          />
                        )}
                      </div>
                    </div>
                  ) : null}
                </li>
              )
            })}
          </ul>
        </Card>
      )}

      {/* Nicht zugeordnete Eintraege */}
      {query.data ? (
        <UnassignedSection
          data={query.data}
          onMap={(portal, portalId, portalName) =>
            setMapTarget({ portal, portalId, portalName })
          }
        />
      ) : null}

      {/* Test-Create / Mapping Dialoge */}
      <TestCreateCustomerModal
        open={testCreatePortal !== null}
        onOpenChange={(v) => {
          if (!v) setTestCreatePortal(null)
        }}
        portal={testCreatePortal ?? 'also'}
        onSuccess={() => query.refetch()}
      />
      <MapCustomerModal
        open={mapTarget !== null}
        onOpenChange={(v) => {
          if (!v) setMapTarget(null)
        }}
        portal={mapTarget?.portal ?? 'also'}
        portalId={mapTarget?.portalId ?? ''}
        portalName={mapTarget?.portalName}
        onSuccess={() => query.refetch()}
      />

      {/* Bulk-Click-Thru-Modal: durchlaeuft Kunden mit Diffs sequentiell. */}
      <BulkPushModal state={bulkPush} />
    </div>
  )
}
/**
 * Hinweis-Block, wenn fuer einen Kunden keine Stammdaten-Vergleichsdaten
 * vorliegen. Zeigt den Grund: laedt noch / Fehler / keine SAP-Verknuepfung
 * in DB / kein Portal-Mapping in DB. So weiss der Anwender, was er tun
 * muss, damit der Abgleich greift.
 */
function MasterDataSyncEmpty({
  isLoading,
  isError,
  customer,
}: {
  isLoading: boolean
  isError: boolean
  customer: CustomerWithProducts
}) {
  if (isLoading) {
    return <div className="text-caption text-gray-500">Vergleichsdaten werden geladen…</div>
  }
  if (isError) {
    return <div className="text-caption text-red-600">Vergleichsdaten konnten nicht geladen werden.</div>
  }
  if (!customer.shmId) {
    return (
      <div className="text-caption text-gray-500">
        Kunde ist nur in SAP angelegt — für den Stammdaten-Abgleich muss
        eine lokale Kunden-Verknüpfung existieren.
      </div>
    )
  }
  if (!customer.externalIds.sap) {
    return (
      <div className="text-caption text-gray-500">
        Kein SAP-CardCode in der lokalen Kunden-DB hinterlegt — Abgleich
        nicht möglich.
      </div>
    )
  }
  if (!customer.externalIds.eset && !customer.externalIds.also) {
    return (
      <div className="text-caption text-gray-500">
        Kein Portal-Mapping (ESET/ALSO) in der lokalen Kunden-DB
        hinterlegt. Die Produkte oben sind ggf. nur per Name-Match
        zugeordnet — ein expliziter Klick auf „Kunde zuordnen" beim
        passenden Lieferanten schreibt die Verknüpfung in die DB, danach
        ist der Abgleich verfügbar.
      </div>
    )
  }
  return (
    <div className="text-caption text-gray-500">
      Keine Vergleichsdaten — möglicherweise ist der Kunde nicht im
      master-data-sync-Filter (SAP-CardCode + Portal-Mapping erforderlich).
    </div>
  )
}

/**
 * Modal fuer den Bulk-Stammdaten-Push. Zeigt Fortschritt + Per-Kunde-
 * Ergebnis und blockiert Schliessen waehrend der Verarbeitung.
 */
function BulkPushModal({ state }: { state: BulkPushState }) {
  const pct = state.total > 0 ? Math.round((state.doneCount / state.total) * 100) : 0
  const isPreview = state.phase === 'preview'
  const isRunning = state.phase === 'running'
  const isDone = state.phase === 'done'

  return (
    <Modal
      open={state.isOpen}
      onOpenChange={(v) => {
        if (!v) state.close()
      }}
      title="Stammdaten-Abgleich"
      description={
        isPreview
          ? `${state.preview.length} Kunde(n) · ${state.selectedCount} Feld${state.selectedCount === 1 ? '' : 'er'} ausgewählt — bitte prüfen, optional einzelne Werte abwählen, dann bestätigen.`
          : isRunning
            ? `Verarbeite ${state.doneCount + 1} von ${state.total}…`
            : `${state.total} Kunde(n) verarbeitet`
      }
      size="xl"
      hideCloseButton={isRunning}
      footer={
        <div className="flex justify-end gap-2">
          {isPreview ? (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={state.close}
              >
                Abbrechen
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  void state.confirm()
                }}
                disabled={state.selectedCount === 0}
              >
                {state.selectedCount} Feld{state.selectedCount === 1 ? '' : 'er'} übertragen
              </Button>
            </>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              onClick={state.close}
              disabled={isRunning}
            >
              Schließen
            </Button>
          )}
        </div>
      }
    >
      {isPreview ? (
        <BulkPushPreview state={state} />
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            {isRunning ? (
              <Loader2 className="h-4 w-4 animate-spin text-brand-600" />
            ) : (
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            )}
            <span className="text-small text-gray-700">
              {isRunning && state.current
                ? `Aktuell: ${state.current.customerName}`
                : isRunning
                  ? 'Starte…'
                  : 'Fertig.'}
            </span>
            <span className="ml-auto text-caption text-gray-500 tabular-nums">
              {state.doneCount}/{state.total} ({pct}%)
            </span>
          </div>
          <div className="h-1.5 w-full bg-gray-100 rounded">
            <div
              className="h-1.5 bg-brand-600 rounded transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="max-h-80 overflow-y-auto rounded border border-gray-200 divide-y divide-gray-100">
            {state.results.length === 0 ? (
              <div className="px-3 py-4 text-caption text-gray-500">
                {isDone ? 'Keine Ergebnisse.' : 'Noch keine Ergebnisse.'}
              </div>
            ) : (
              state.results.map((r) => (
                <div key={r.customerId} className="px-3 py-2">
                  <div className="text-small font-medium text-gray-900">
                    {r.customerName}
                  </div>
                  <ul className="ml-2 mt-1 space-y-0.5">
                    {r.portals.length === 0 ? (
                      <li className="text-caption text-gray-500">Keine pushbaren Diffs.</li>
                    ) : (
                      r.portals.map((p, i) => (
                        <li
                          key={`${p.portal}-${i}`}
                          className="flex items-center gap-2 text-caption"
                        >
                          {p.result.ok ? (
                            <CheckCircle2 className="h-3 w-3 text-green-500" />
                          ) : (
                            <XCircle className="h-3 w-3 text-red-500" />
                          )}
                          <span className="font-medium uppercase tracking-wide">
                            {PORTAL_LABEL[p.portal]}
                          </span>
                          {p.result.ok ? (
                            <span className="text-gray-600">
                              Felder: {p.result.pushedFields.join(', ') || '(keine)'}
                            </span>
                          ) : (
                            <span className="text-red-600 truncate" title={p.result.error}>
                              {p.result.error}
                            </span>
                          )}
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </Modal>
  )
}

/**
 * Vorschau-Tabelle pro Kunde:
 *
 *  | Feld | SAP (neu) | ESET | ALSO | … |
 *
 * Pro MSP-Spalte: bei Diff Checkbox + alter Wert (durchgestrichen, grau)
 * + neuer Wert (orange). Bei „synchron" oder „nicht pushbar" steht ein
 * neutraler Hinweis. Ueber die Checkbox kann der User einzelne Werte
 * vom Push ausschliessen — Default ist „alle Diffs aktiviert".
 *
 * Reihenfolge der MSP-Spalten ist stabil ('eset' vor 'also'); kommen
 * weitere MSPs hinzu, einfach in `MSP_COLUMN_ORDER` ergaenzen.
 */
const MSP_COLUMN_ORDER: Portal[] = ['eset', 'also']

function BulkPushPreview({ state }: { state: BulkPushState }) {
  return (
    <div className="max-h-[60vh] overflow-y-auto rounded border border-gray-200 divide-y divide-gray-200">
      {state.preview.map((c) => (
        <BulkPushCustomerTable key={c.customerId} customer={c} state={state} />
      ))}
    </div>
  )
}

function BulkPushCustomerTable({
  customer,
  state,
}: {
  customer: CustomerComparison
  state: BulkPushState
}) {
  // Spalten: nur Portale anzeigen, die fuer diesen Kunden ueberhaupt
  // konfiguriert/erreichbar sind. Reihenfolge stabil ueber MSP_COLUMN_ORDER.
  const portalsByKey = new Map<Portal, (typeof customer.portals)[number]>()
  for (const p of customer.portals) portalsByKey.set(p.portal, p)
  const visiblePortals: Portal[] = MSP_COLUMN_ORDER.filter((id) => portalsByKey.has(id))

  // Zeilen: Vereinigung aller Felder, bei denen mindestens ein Portal
  // einen Diff hat. Wenn der Kunde keine Diffs hat (sollte nicht
  // passieren weil wir ihn dann nicht in preview haetten), zeigen wir
  // alle Felder zur Klarheit an.
  const fieldOrder: FieldKey[] = ['name', 'email', 'phone', 'city', 'zip', 'country']
  const fieldLabel: Record<FieldKey, string> = {
    name: 'Firmenname',
    email: 'E-Mail',
    phone: 'Telefon',
    city: 'Stadt',
    zip: 'PLZ',
    country: 'Land',
  }
  const rowFields = fieldOrder.filter((f) =>
    visiblePortals.some((portal) => {
      const pf = portalsByKey.get(portal)?.fields.find((x) => x.field === f)
      return pf?.differs && pf.pushable
    }),
  )

  // SAP-Wert pro Feld: alle Portale haben den gleichen sapValue fuer ein
  // Feld, wir nehmen den ersten verfuegbaren.
  function sapValueFor(f: FieldKey): string | null {
    for (const portal of visiblePortals) {
      const pf = portalsByKey.get(portal)?.fields.find((x) => x.field === f)
      if (pf) return pf.sapValue
    }
    return null
  }

  return (
    <div className="px-3 py-3">
      <div className="text-small font-semibold text-gray-900 mb-2">
        {customer.customerName}
      </div>
      {rowFields.length === 0 ? (
        <div className="text-caption text-gray-500">Keine pushbaren Diffs.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-caption border-collapse">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">
                  Feld
                </th>
                <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">
                  SAP
                </th>
                {visiblePortals.map((portal) => (
                  <th
                    key={portal}
                    className="text-left px-2 py-1.5 font-medium border-b border-gray-200 uppercase tracking-wide"
                  >
                    {PORTAL_LABEL[portal]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rowFields.map((f) => (
                <tr key={f} className="border-b border-gray-100 last:border-b-0">
                  <td className="px-2 py-1.5 text-gray-700">{fieldLabel[f]}</td>
                  <td className="px-2 py-1.5 text-gray-900 font-medium">
                    {sapValueFor(f) || '—'}
                  </td>
                  {visiblePortals.map((portal) => {
                    const pf = portalsByKey.get(portal)?.fields.find((x) => x.field === f)
                    return (
                      <td key={portal} className="px-2 py-1.5">
                        <PortalCell
                          customerId={customer.customerId}
                          portal={portal}
                          field={f}
                          diff={pf}
                          state={state}
                        />
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function PortalCell({
  customerId,
  portal,
  field,
  diff,
  state,
}: {
  customerId: string
  portal: Portal
  field: FieldKey
  diff: FieldDiff | undefined
  state: BulkPushState
}) {
  if (!diff) {
    return <span className="text-caption text-gray-400">—</span>
  }
  if (!diff.differs) {
    return <span className="text-caption text-gray-400">synchron</span>
  }
  if (!diff.pushable) {
    return (
      <span
        className="text-caption text-gray-400"
        title={`Portal ${PORTAL_LABEL[portal]} unterstützt das Feld nicht im Update.`}
      >
        nicht pushbar
      </span>
    )
  }
  const checked = state.isSelected(customerId, portal, field)
  return (
    <label
      className={`flex items-start gap-2 cursor-pointer ${checked ? '' : 'opacity-50'}`}
    >
      <input
        type="checkbox"
        className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300"
        checked={checked}
        onChange={() => state.toggle(customerId, portal, field)}
      />
      <span className="flex flex-col leading-tight">
        <span className="text-gray-500 line-through">{diff.portalValue || '—'}</span>
        <span className="text-amber-700 font-medium">{diff.sapValue || '—'}</span>
      </span>
    </label>
  )
}

function groupBySupplier(
  products: SupplierProduct[],
  filter: Set<SupplierId>,
): Map<SupplierId, SupplierProduct[]> {
  const map = new Map<SupplierId, SupplierProduct[]>()
  for (const p of products) {
    if (filter.size > 0 && !filter.has(p.supplierId)) continue
    const list = map.get(p.supplierId) ?? []
    list.push(p)
    map.set(p.supplierId, list)
  }
  return map
}

function UnassignedSection({
  data,
  onMap,
}: {
  data: SupplierOverviewResponse
  onMap: (portal: 'also' | 'eset', portalId: string, portalName?: string) => void
}) {
  const [open, setOpen] = useState(false)
  const total =
    data.unassigned.also.length +
    data.unassigned.eset.length +
    data.unassigned.cwa.length +
    (data.unassigned.kyberio?.length ?? 0)
  if (total === 0) return null

  const allSections: Array<{ id: SupplierId; label: string; items: SupplierProduct[] }> = [
    { id: 'also', label: 'ALSO', items: data.unassigned.also },
    { id: 'eset', label: 'ESET', items: data.unassigned.eset },
    { id: 'cwa', label: 'CWA', items: data.unassigned.cwa },
    { id: 'kyberio', label: 'Kyberio / Domains', items: data.unassigned.kyberio ?? [] },
  ]
  const sections = allSections.filter((s) => s.items.length > 0)

  return (
    <Card className="border-amber-200">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <span className="font-medium text-gray-900">
            {total} Einträge ohne Kundenzuordnung
          </span>
        </div>
        {open ? (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-400" />
        )}
      </button>
      {open ? (
        <div className="border-t border-amber-100 bg-amber-50/30 px-4 py-3 space-y-3">
          <p className="text-caption text-gray-600">
            Diese Lieferanten-Einträge konnten keinem unserer Kunden zugeordnet werden — entweder
            fehlt die externe ID am Kunden oder der Name weicht ab. Zuordnung in der
            Kundenverwaltung pflegen.
          </p>
          {sections.map((sec) => (
            <div key={sec.id}>
              <div className="flex items-center gap-2 mb-1">
                <span className={`h-2 w-2 rounded-full ${SUPPLIER_DOT_CLASS[sec.id]}`} />
                <span className="text-caption font-medium text-gray-700 uppercase tracking-wide">
                  {sec.label}
                </span>
                <span className="text-caption text-gray-400">{sec.items.length}</span>
              </div>
              <ul className="ml-4 space-y-1">
                {sec.items.slice(0, 50).map((p, idx) => (
                  <UnassignedRow
                    key={`${p.externalId ?? 'noid'}-${idx}`}
                    product={p}
                    supplierId={sec.id}
                    onMap={onMap}
                  />
                ))}
                {sec.items.length > 50 ? (
                  <li className="text-caption text-gray-500 ml-4">
                    … und {sec.items.length - 50} weitere
                  </li>
                ) : null}
              </ul>
            </div>
          ))}
        </div>
      ) : null}
    </Card>
  )
}

// Eine einzelne unzugeordnete Lieferanten-Zeile mit aufklappbarem
// Detail-Bereich. ESET hat einen „EMA-Detail laden"-Button (laedt erst auf
// Klick — sonst wuerde die Uebersicht N parallele Calls absetzen).
function UnassignedRow({
  product,
  supplierId,
  onMap,
}: {
  product: SupplierProduct
  supplierId: SupplierId
  onMap: (portal: 'also' | 'eset', portalId: string, portalName?: string) => void
}) {
  // Fuer das Mapping/Writeback brauchen wir die Customer-Level-ID
  // (company.AccountId bei ALSO, EMA companyId bei ESET) — NICHT die
  // license/subscription-ID. Fallback auf externalId nur wenn customer-id
  // fehlt (sollte fuer mappingfaehige Eintraege eigentlich nicht passieren).
  const mapId = product.externalCustomerId ?? product.externalId
  const canMap = (supplierId === 'also' || supplierId === 'eset') && !!mapId
  const [open, setOpen] = useState(false)
  const detail = useQuery<unknown>({
    queryKey: ['eset-company-detail', product.externalCustomerId],
    queryFn: () =>
      api.get(`/eset/companies/${product.externalCustomerId}/detail`).then((r) => r.data),
    enabled: false,
    retry: false,
  })
  const canExpand = supplierId === 'eset' && Boolean(product.externalCustomerId)

  return (
    <li className="text-small">
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-2 min-w-0">
          {canExpand ? (
            <button
              type="button"
              onClick={() => {
                if (!open && !detail.data) detail.refetch()
                setOpen((v) => !v)
              }}
              className="text-gray-400 hover:text-gray-700 flex-shrink-0"
              aria-label={open ? 'Detail einklappen' : 'EMA-Detail laden'}
            >
              {open ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
            </button>
          ) : (
            <ExternalLink className="h-3 w-3 text-gray-400 flex-shrink-0" />
          )}
          <span className="text-gray-900 truncate">{product.productName}</span>
          {product.externalCustomerName ? (
            <span className="text-gray-500 truncate">({product.externalCustomerName})</span>
          ) : null}
          {product.externalCustomerId ? (
            <span className="text-caption text-gray-400 font-mono">
              {product.externalCustomerId.slice(0, 8)}…
            </span>
          ) : null}
        </span>
        <span className="flex items-center gap-2 text-gray-500 flex-shrink-0">
          {product.detail ? <span className="tabular-nums">{product.detail}</span> : null}
          {productStatusBadge(product)}
          {canMap ? (
            <Button
              variant="ghost"
              size="xs"
              onClick={() =>
                onMap(
                  supplierId as 'also' | 'eset',
                  mapId!,
                  product.externalCustomerName,
                )
              }
            >
              SAP zuordnen
            </Button>
          ) : null}
        </span>
      </div>
      {canExpand && open ? (
        <div className="ml-5 mt-1 mb-2 rounded border border-amber-200 bg-white p-2">
          {detail.isFetching ? (
            <div className="text-caption text-gray-500">Lade EMA-Customer-Detail …</div>
          ) : detail.error ? (
            <div className="text-caption text-red-600">
              Fehler:{' '}
              {detail.error instanceof Error ? detail.error.message : String(detail.error)}
            </div>
          ) : detail.data ? (
            <CompanyDetailView data={detail.data} />
          ) : null}
        </div>
      ) : null}
    </li>
  )
}

// Strukturierte Anzeige von /api/Company/Detail. Schiebt die wichtigsten
// Felder (customIdentifier, name, address, etc.) nach oben — die Roh-Antwort
// bleibt darunter immer komplett sichtbar (kein scroll-cap).
function CompanyDetailView({ data }: { data: unknown }) {
  const obj = data && typeof data === 'object' ? (data as Record<string, unknown>) : {}
  function pick(...keys: string[]): string | null {
    for (const key of keys) {
      const v = obj[key]
      if (typeof v === 'string' && v.trim()) return v.trim()
      if (typeof v === 'number') return String(v)
    }
    return null
  }
  const customIdentifier = pick('customIdentifier', 'CustomIdentifier', 'custom_identifier')
  const name = pick('name', 'Name')
  const email = pick('email', 'Email')
  const vatId = pick('vatId', 'VatId', 'vat_id')
  const description = pick('description', 'Description')
  const comment = pick('comment', 'Comment')
  const status = pick('status', 'Status')
  const type = pick('type', 'Type')
  const companyId = pick('companyId', 'CompanyId')

  // Adresse oft als verschachteltes Objekt
  const addrRaw =
    (obj.address as Record<string, unknown> | undefined) ??
    (obj.Address as Record<string, unknown> | undefined) ??
    {}
  function addrStr(...keys: string[]): string | null {
    for (const k of keys) {
      const v = addrRaw[k]
      if (typeof v === 'string' && v.trim()) return v.trim()
    }
    return null
  }
  const street = addrStr('streetAddress', 'StreetAddress', 'street')
  const zip = addrStr('zipCode', 'ZipCode', 'zip', 'postalCode')
  const city = addrStr('city', 'City')
  const state = addrStr('state', 'State')

  const json = JSON.stringify(data, null, 2)
  const [copied, setCopied] = useState(false)
  function copy() {
    navigator.clipboard.writeText(json).then(
      () => {
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      },
      () => {
        /* ignore */
      },
    )
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-1 text-caption">
        <DetailField
          label="customIdentifier"
          value={customIdentifier}
          highlight={!customIdentifier ? 'red' : 'green'}
        />
        <DetailField label="Name" value={name} />
        <DetailField label="companyId" value={companyId} mono />
        <DetailField label="email" value={email} />
        <DetailField label="vatId" value={vatId} />
        <DetailField label="status" value={status} />
        <DetailField label="type" value={type} />
        <DetailField label="description" value={description} />
        <DetailField label="comment" value={comment} />
        <DetailField
          label="Adresse"
          value={
            [street, zip && city ? `${zip} ${city}` : zip || city, state]
              .filter(Boolean)
              .join(', ') || null
          }
        />
      </div>
      <details>
        <summary className="cursor-pointer text-caption font-medium text-gray-700">
          Roh-JSON ({json.length} Zeichen)
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              copy()
            }}
            className="ml-2 text-brand-600 hover:underline"
          >
            {copied ? 'kopiert ✓' : 'kopieren'}
          </button>
        </summary>
        <pre className="text-xs mt-1 overflow-auto bg-gray-50 p-2 border whitespace-pre-wrap break-all">
          {json}
        </pre>
      </details>
    </div>
  )
}

function DetailField({
  label,
  value,
  highlight,
  mono,
}: {
  label: string
  value: string | null
  highlight?: 'red' | 'green'
  mono?: boolean
}) {
  const valueClass =
    highlight === 'red'
      ? 'text-red-600 font-medium'
      : highlight === 'green'
        ? 'text-green-700 font-medium'
        : 'text-gray-900'
  return (
    <div className="flex items-baseline gap-1.5 min-w-0">
      <span className="text-gray-500 flex-shrink-0">{label}:</span>
      <span className={`${valueClass} ${mono ? 'font-mono text-xs' : ''} truncate`}>
        {value ?? <span className="text-gray-400">—</span>}
      </span>
    </div>
  )
}

function formatRelativeAge(isoTimestamp: string | null | undefined): string {
  if (!isoTimestamp) return '—'
  const then = new Date(isoTimestamp).getTime()
  if (!Number.isFinite(then)) return '—'
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000))
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes} min`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `${hours} h`
  const days = Math.round(hours / 24)
  return `${days} Tagen`
}
